import React, { useEffect, useMemo, useRef, useState } from 'react';

import { Input, Popover, message } from 'antd';

import styled from 'styled-components';

import { EventSubTypeColorsList } from '@constants/SwatchColors';

import {
    DEFAULT_FOLDER_COLOR,
    getFolderColor,
    getFolderColorPayload,
    getFolderContacts,
    isFolderOfType,
} from '@libs/NwFolders';

import {
    useAddContactsToFolder,
    useCreateFolder,
    useFolders,
    useRemoveContactsFromFolder,
} from '@hooks/API/UseFolders';

import FolderPrivacyIcon from '@components/ContactsFolders/FolderPrivacyIcon';
import { NwButton } from '@components/Gui/NwButton';
import SwatchPicker from '@components/Gui/NwForm/SwatchPicker';
import NwIcon from '@components/Gui/NwIcon';
import { NwMiniAddButton, NwMiniButton, NwMiniRemoveButton } from '@components/Gui/NwMiniButton';

import { byPrefixAndName } from '@awesome.me/kit-d6dae216cd/icons';

const Content = styled.div`
    width: ${(props) => (props.$wide ? '32rem' : '24rem')};
    font-size: ${(props) => props.theme.FontSizeSm};
`;

const SectionLabel = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
    padding: 0.75rem 0.75rem 0.25rem;
    text-transform: uppercase;
    font-size: ${(props) => props.theme.FontSizeXSm};
    letter-spacing: 0.05em;
    color: ${(props) => props.theme.TextColorMuted};

    &.header {
        padding: 0.5rem 0.75rem;
        border-bottom: ${(props) => props.theme.DefaultBorder};
    }
`;

//max-height con scroll: il footer di creazione resta sempre a vista anche con molte folder
const FoldersList = styled.div`
    max-height: 18rem;
    overflow-y: auto;
`;

const FolderRow = styled.div`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.3rem 0.75rem;
    background-color: ${(props) => (props.$highlighted ? props.theme.HoverablesBackground : 'transparent')};
    transition: background-color 0.3s;

    .folder-color {
        width: 0.5rem;
        height: 0.5rem;
        border-radius: 50%;
        flex: none;
    }

    //nome e icona privacy stanno insieme come nel select delle folder in sidebar, non ai due estremi
    .folder-label {
        flex: 1;
        min-width: 0;
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .folder-name {
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        text-transform: uppercase;
    }

    .folder-feedback {
        flex: none;
        font-size: ${(props) => props.theme.FontSizeXSm};
        text-transform: uppercase;
        color: ${(props) => props.theme.PrimaryColor};

        &.removed {
            color: ${(props) => props.theme.DangerColor};
        }
    }

    .folder-match {
        flex: none;
        font-size: ${(props) => props.theme.FontSizeXSm};
        color: ${(props) => props.theme.TextColorMuted};
        white-space: nowrap;
    }

    .folder-actions {
        flex: none;
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 2rem;
    }

    .folder-confirm {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .folder-confirm-text {
        text-transform: uppercase;
        font-size: ${(props) => props.theme.FontSizeXSm};
        color: ${(props) => props.theme.DangerColor};
        white-space: nowrap;
    }
`;

const EmptyFolders = styled.div`
    padding: 1.5rem 0.75rem;
    text-align: center;
    color: ${(props) => props.theme.TextColorMuted};
`;

const Footer = styled.div`
    padding: 0.5rem 0.75rem;
    border-top: ${(props) => props.theme.DefaultBorder};

    .create-fields {
        display: flex;
        align-items: center;
        gap: 0.5rem;
    }

    .create-error {
        display: block;
        margin-top: 0.25rem;
        color: ${(props) => props.theme.DangerColor};
        font-size: ${(props) => props.theme.FontSizeXSm};
    }

    .create-buttons {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 0.5rem;
        margin-top: 0.75rem;
    }
`;

const FolderBadge = styled.div`
    font-size: 1rem;
    .ant-badge-count {
        background: ${(props) => props.theme.PrimaryColor};
        margin-top: 2px;
    }
    .ant-badge-multiple-words {
        padding: 0 3px;
    }
`;

const getContactsLabel = (count, type) =>
    `${count} ${type === 'customer' ? 'customer' : 'model'}${count > 1 ? 's' : ''}`;

const FEEDBACK_DURATION = 2500;

const AddToFolderButton = ({ ids, type, placement, size, showBadge, text, onFolderCreated, disabled, wide }) => {
    //durante il primo caricamento la lista non c'e' ancora: il pulsante resta comunque disponibile
    const { data } = useFolders();
    const folders = data || [];
    const { mutateAsync: addContactsToFolder } = useAddContactsToFolder();
    const { mutateAsync: removeContactsFromFolder } = useRemoveContactsFromFolder();
    const { mutateAsync: createFolder } = useCreateFolder();

    const [busyFolderID, setBusyFolderID] = useState(null);
    const [confirmingFolderID, setConfirmingFolderID] = useState(null);
    const [feedback, setFeedback] = useState(null);
    const [isCreating, setIsCreating] = useState(false);
    const [newFolderName, setNewFolderName] = useState('');
    const [newFolderColor, setNewFolderColor] = useState(DEFAULT_FOLDER_COLOR);
    const [createError, setCreateError] = useState(null);
    const [isSaving, setIsSaving] = useState(false);

    const feedbackTimeout = useRef(null);

    useEffect(() => {
        return () => window.clearTimeout(feedbackTimeout.current);
    }, []);

    const isMultiple = ids.length > 1;

    //dopo l'azione la riga cambia sezione: l'evidenziazione serve a far seguire lo spostamento
    const showFeedback = (folderID, action) => {
        setFeedback({ folderID: folderID, action: action });
        window.clearTimeout(feedbackTimeout.current);
        feedbackTimeout.current = window.setTimeout(() => setFeedback(null), FEEDBACK_DURATION);
    };

    const typeFolders = useMemo(() => folders.filter((folder) => isFolderOfType(folder, type)), [folders, type]);

    //una folder sta tra quelle "in these folders" solo se contiene tutti i contatti selezionati
    const { inFolders, outFolders } = useMemo(() => {
        const inList = [];
        const outList = [];
        for (const folder of typeFolders) {
            const folderContacts = getFolderContacts(folder);
            const row = {
                folder: folder,
                insideIDs: ids.filter((id) => folderContacts.includes(id)),
                missingIDs: ids.filter((id) => !folderContacts.includes(id)),
            };
            if (row.missingIDs.length === 0) {
                inList.push(row);
            } else {
                outList.push(row);
            }
        }
        return { inFolders: inList, outFolders: outList };
    }, [typeFolders, ids]);

    const handleAdd = async ({ folder, missingIDs }) => {
        setBusyFolderID(folder.ID);
        try {
            await addContactsToFolder({ folderID: folder.ID, contactIDs: missingIDs });
            showFeedback(folder.ID, 'added');
        } catch (error) {
            console.error('Add Folder Contacts Error => ', error);
            message.error(`Couldn't add to ${folder.Name}`);
        }
        setBusyFolderID(null);
    };

    const handleRemove = async ({ folder, insideIDs }) => {
        setConfirmingFolderID(null);
        setBusyFolderID(folder.ID);
        try {
            await removeContactsFromFolder({ folderID: folder.ID, contactIDs: insideIDs });
            showFeedback(folder.ID, 'removed');
        } catch (error) {
            console.error('Remove Folder Contacts Error => ', error);
            message.error(`Couldn't remove from ${folder.Name}`);
        }
        setBusyFolderID(null);
    };

    const handleOpenCreateForm = () => {
        setIsCreating(true);
    };

    const handleCloseCreateForm = () => {
        setIsCreating(false);
        setNewFolderName('');
        setNewFolderColor(DEFAULT_FOLDER_COLOR);
        setCreateError(null);
    };

    const handleChangeNewFolderName = (event) => {
        setNewFolderName(event.target.value);
        setCreateError(null);
    };

    //la folder nasce gia' con dentro i contatti selezionati: una sola azione invece di crea e poi aggiungi
    const handleCreateFolder = async () => {
        const name = newFolderName.trim();
        if (!name) {
            setCreateError('Enter a name');
            return;
        }
        setIsSaving(true);
        try {
            const newFolder = await createFolder({
                Name: name,
                Description: '',
                Type: type || 'model',
                Color: getFolderColorPayload(newFolderColor),
                PrivacyLevel: 'User',
                ContactIDs: [...ids],
            });
            showFeedback(newFolder.ID, 'added');
            if (onFolderCreated) {
                onFolderCreated(newFolder.ID, newFolder.Name);
            }
            handleCloseCreateForm();
        } catch (error) {
            console.error('Create Folder Error => ', error);
            message.error("Couldn't create the folder");
        }
        setIsSaving(false);
    };

    const renderFolderRow = (row, action) => {
        const { folder, insideIDs, missingIDs } = row;
        const rowFeedback = feedback && feedback.folderID === folder.ID ? feedback.action : null;
        const isBusy = busyFolderID === folder.ID;
        const isConfirming = confirmingFolderID === folder.ID;

        return (
            <FolderRow key={folder.ID} $highlighted={rowFeedback ? true : false}>
                <span className="folder-color" style={{ backgroundColor: getFolderColor(folder.Color) }} />
                <span className="folder-label">
                    <span className="folder-name">{folder.Name}</span>
                    <FolderPrivacyIcon folder={folder} size="sm" />
                </span>
                {rowFeedback && <span className={`folder-feedback ${rowFeedback}`}>just {rowFeedback}</span>}
                {isMultiple && !rowFeedback && (
                    <span className="folder-match">
                        {insideIDs.length} of {ids.length}
                    </span>
                )}
                <span className="folder-actions">
                    {isConfirming ? (
                        <span className="folder-confirm">
                            <span className="folder-confirm-text">are you sure</span>
                            <NwMiniButton variant="danger" label="yes" onClick={() => handleRemove(row)} />
                            <NwMiniButton
                                outlined
                                variant="neutral"
                                label="no"
                                onClick={() => setConfirmingFolderID(null)}
                            />
                        </span>
                    ) : action === 'add' ? (
                        <>
                            <NwMiniAddButton
                                label={isMultiple ? `add ${missingIDs.length}` : 'add'}
                                loading={isBusy}
                                onClick={() => handleAdd(row)}
                            />
                            {/* in multi-selezione il remove resta sempre in riga, disabilitato quando non c'e'
                                nulla da togliere, cosi' le colonne di pulsanti restano incolonnate */}
                            {isMultiple && (
                                <NwMiniRemoveButton
                                    disabled={insideIDs.length === 0}
                                    label={`remove ${insideIDs.length}`}
                                    loading={isBusy}
                                    onClick={() => setConfirmingFolderID(folder.ID)}
                                />
                            )}
                        </>
                    ) : (
                        <NwMiniRemoveButton
                            label={isMultiple ? 'remove all' : 'remove'}
                            loading={isBusy}
                            onClick={() => (isMultiple ? setConfirmingFolderID(folder.ID) : handleRemove(row))}
                        />
                    )}
                </span>
            </FolderRow>
        );
    };

    const popoverContent = (
        <Content $wide={wide ? true : false}>
            <SectionLabel className="header">
                <span>folders</span>
                <span>
                    {isMultiple
                        ? `${getContactsLabel(ids.length, type)} selected`
                        : `in ${inFolders.length} of ${typeFolders.length}`}
                </span>
            </SectionLabel>
            {typeFolders.length === 0 ? (
                <EmptyFolders>No folders yet. Create the first one below.</EmptyFolders>
            ) : (
                <FoldersList>
                    {inFolders.length > 0 && (
                        <>
                            <SectionLabel>in these folders</SectionLabel>
                            {inFolders.map((row) => renderFolderRow(row, 'remove'))}
                        </>
                    )}
                    {outFolders.length > 0 && (
                        <>
                            <SectionLabel>add to</SectionLabel>
                            {outFolders.map((row) => renderFolderRow(row, 'add'))}
                        </>
                    )}
                </FoldersList>
            )}
            <Footer>
                {isCreating ? (
                    <>
                        <div className="create-fields">
                            <Input
                                autoFocus
                                placeholder="Folder name"
                                value={newFolderName}
                                onChange={handleChangeNewFolderName}
                                onPressEnter={handleCreateFolder}
                            />
                            <SwatchPicker
                                compact
                                placement="left"
                                colorsList={EventSubTypeColorsList}
                                defaultColor={newFolderColor}
                                value={newFolderColor}
                                onChange={setNewFolderColor}
                            />
                        </div>
                        {createError && <span className="create-error">{createError}</span>}
                        <div className="create-buttons">
                            <NwMiniButton
                                outlined
                                variant="neutral"
                                icon={byPrefixAndName.fass['times']}
                                label="cancel"
                                onClick={handleCloseCreateForm}
                            />
                            <NwMiniButton
                                icon={byPrefixAndName.fass['folder-plus']}
                                label={isMultiple ? `create and add ${ids.length}` : 'create and add'}
                                loading={isSaving}
                                onClick={handleCreateFolder}
                            />
                        </div>
                    </>
                ) : (
                    <NwButton
                        ghost
                        primary
                        size="small"
                        icon={byPrefixAndName.fass['folder-plus']}
                        label="create new folder"
                        onClick={handleOpenCreateForm}
                    />
                )}
            </Footer>
        </Content>
    );

    return (
        <Popover placement={placement} overlayClassName="folder-popover" trigger="click" content={popoverContent}>
            {showBadge ? (
                <FolderBadge>
                    {text ? (
                        <NwButton ghost disabled={disabled} icon={byPrefixAndName.fasl['folder']} label={text} />
                    ) : (
                        <NwIcon
                            badge={isMultiple ? 0 : inFolders.length}
                            disabled={disabled}
                            icon={byPrefixAndName.fasl['folder']}
                            size={size}
                        />
                    )}
                </FolderBadge>
            ) : text ? (
                <NwButton ghost disabled={disabled} icon={byPrefixAndName.fasl['folder']} label={text} />
            ) : (
                <NwIcon disabled={disabled} icon={byPrefixAndName.fasl['folder']} size={size} />
            )}
        </Popover>
    );
};

export default AddToFolderButton;
